import cx from "classnames";
import { forwardRef } from "react";

import Box, { BoxProps } from "ds/components/Box";
import { ChevronRight } from "components/icons/generated";

import styles from "./styles.module.css";
import ButtonIcon, { ButtonIconProps } from "../ButtonIcon";

type TreeProps = BoxProps & {
  compact?: boolean;
};

const Tree = ({ children, className, ...rest }: TreeProps) => {
  return (
    <Box direction="column" className={cx(styles.tree, className)} {...rest}>
      {children}
    </Box>
  );
};

Tree.displayName = "DS.Tree";

type TreeItemProps = BoxProps & {
  isSelected?: boolean;
  isDisabled?: boolean;
  isFocusHidden?: boolean;
  isInteractive?: boolean;
  isIndented?: boolean;
  autoHeight?: boolean;
};

export const TreeItem = forwardRef<HTMLDivElement, TreeItemProps>(
  (
    {
      children,
      className,
      isSelected,
      isDisabled,
      isFocusHidden,
      isInteractive,
      isIndented,
      autoHeight,
      ...rest
    }: TreeItemProps,
    ref
  ) => {
    return (
      <Box
        fullWidth
        align="center"
        className={cx(
          styles.treeItem,
          {
            [styles.selected]: isSelected,
            [styles.disabled]: isDisabled,
            [styles.focusHidden]: isFocusHidden,
            [styles.indented]: isIndented,
            [styles.interactive]: isInteractive,
            [styles.autoHeight]: autoHeight,
          },
          className
        )}
        ref={ref}
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

TreeItem.displayName = "DS.Tree.Item";

type TreeItemToggleProps = Omit<ButtonIconProps, "icon" | "iconRotate" | "variant" | "children"> & {
  isExpanded?: boolean;
  children?: string;
};

export const TreeItemToggle = ({
  children = "Toggle group",
  className,
  isExpanded,
  ...rest
}: TreeItemToggleProps) => {
  return (
    <ButtonIcon
      icon={ChevronRight}
      iconRotate={isExpanded ? "90" : undefined}
      variant="ghost"
      excludeFromTabOrder
      className={cx(styles.toggle, className)}
      {...rest}
    >
      {children}
    </ButtonIcon>
  );
};

TreeItemToggle.displayName = "DS.Tree.Item.Toggle";

export const TreeItemContent = ({ children, className, ...rest }: BoxProps) => {
  return (
    <Box align="center" className={cx(styles.content, className)} fullWidth {...rest}>
      {children}
    </Box>
  );
};

TreeItemContent.displayName = "DS.Tree.Item.Content";

type TreeBranchProps = BoxProps & {
  isFirst?: boolean;
};

export const TreeBranch = ({ children, className, isFirst, ...rest }: TreeBranchProps) => {
  return (
    <Box
      direction="column"
      fullWidth
      {...rest}
      className={cx(styles.branch, isFirst && styles.first, className)}
    >
      {children}
    </Box>
  );
};

TreeBranch.displayName = "DS.Tree.Branch";

type TreeBranchLinkProps = BoxProps & {
  full?: boolean;
};

export const TreeBranchLink = ({ children, className, ...rest }: TreeBranchLinkProps) => {
  return (
    <Box fullWidth {...rest} className={cx(styles.branchLink, className)}>
      {children}
    </Box>
  );
};

TreeBranchLink.displayName = "DS.Tree.BranchLink";

export default Tree;
